import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames/bind';
import Image from '../../../refresh/atoms/ImageView';
import { segmentEventPush } from '../../../modules/ga/GTMdatalayer';

import styles from './index.pcss';

const TRENDS_PLP_WIDGET_NAME = 'trends_plp_widget';
const TRENDS_PLP_WIDGET_ID = 'trends-plp-widget';
let widgetViewedFired = false;

const cx = classNames.bind(styles);

export interface TrendItem {
  trendId: string;
  displayName: string;
  image: string;
  description: string;
  redirectQuery: string;
}

interface TrendsPLPSliderProps {
  trends: TrendItem[];
  defaultSelectedIndex?: number;
  onTrendSelect?: (index: number, trend: TrendItem) => void;
  luxe: boolean;
  fleek: boolean;
  gradientStart?: string;
  gradientEnd?: string;
}

const TrendsPLPSlider: FC<TrendsPLPSliderProps> = ({
  trends = [],
  defaultSelectedIndex = 0,
  onTrendSelect,
  luxe = false,
  fleek = false,
  gradientStart= '',
  gradientEnd= ''
}) => {
  const searchQuery = typeof window !== 'undefined' ? window.location.search : '';
  const [selectedIndex, setSelectedIndex] = useState(0);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  const resolvedDefaultIndex = () => {
    const trendIdFromQuery = String(
      typeof window !== 'undefined' ? new URLSearchParams(searchQuery).get('trendId') || '' : ''
    )
      .trim()
      .toLowerCase()
      .replace(/^#/, '');

    const trendName = String(
      typeof window !== 'undefined' ? new URLSearchParams(searchQuery).get('tagName') || '' : ''
    );

    if (trendIdFromQuery && trends.length > 0) {
      const matchedIndex = trends.findIndex(
        trend =>
          String(trend.trendId || '')
            .trim()
            .toLowerCase()
            .replace(/^#/, '') === trendIdFromQuery
      );

      if (matchedIndex >= 0) {
        setSelectedIndex(matchedIndex);
        return;
      }
    }

    if (trendName && trends.length > 0) {
      const matchedIndex = trends.findIndex(
        trend => String(trend.displayName || '') === trendName
      );

      if (matchedIndex >= 0) {
        setSelectedIndex(matchedIndex);
        return;
      }
    }

    if (defaultSelectedIndex >= 0 && defaultSelectedIndex < trends.length) {
      setSelectedIndex(defaultSelectedIndex);
      return;
    }
    setSelectedIndex(0);
  };

  useEffect(()=>{
    resolvedDefaultIndex();
  },[trends, defaultSelectedIndex, searchQuery])

  const selectedTrend = trends[selectedIndex];

  const buildWidgetTiles = () =>
    trends.map((trend, index) => ({
      tile_id: trend.trendId || '',
      tile_name: trend.displayName || '',
      tile_position: index + 1,
      tile_clickable: true,
      tile_type: 'trend',
      tile_product_options_available: ''
    }));

  useEffect(() => {
    if (trends.length === 0) return;
    const container = document.getElementById(TRENDS_PLP_WIDGET_ID);
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !widgetViewedFired) {
          widgetViewedFired = true;
          segmentEventPush({
            event: 'Widget Viewed',
            widget_name: TRENDS_PLP_WIDGET_NAME,
            widget_tiles: buildWidgetTiles(),
            total_widget_tiles: trends.length
          });
          observer.disconnect();
        }
      },
      { threshold: 1 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [trends]);

  useEffect(() => {
    const card = cardRefs?.current?.[selectedIndex];
    const carousel = carouselRef.current;
    if (card && carousel) {
      carousel.scrollTo({ left: card.offsetLeft - 22, behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const handleCardClick = useCallback(
    (index: number) => {
      const trend = trends[index];
      segmentEventPush({
        event: 'Widget Clicked',
        widget_name: TRENDS_PLP_WIDGET_NAME,
        widget_tiles: [{
          tile_id: trend?.trendId || '',
          tile_name: trend?.displayName || '',
          tile_position: index + 1,
          tile_clickable: true,
          tile_type: 'trend',
          tile_product_options_available: ''
        }],
        total_widget_tiles: trends.length
      });
      setSelectedIndex(index);
      onTrendSelect?.(index, trend);
    },
    [onTrendSelect, trends]
  );

  if (!trends?.length) {
    return null;
  }

  return (
    <div id={TRENDS_PLP_WIDGET_ID}>
      <div
        className={cx(
          'trendsSlider',
          luxe ? 'luxeTrendsSlider' : '',
          fleek ? 'fleekTrendsSlider' : ''
        )}
        style={
          {
            '--slider-gradient-start': gradientStart,
            '--slider-gradient-end': gradientEnd
          } as React.CSSProperties
        }
      >
        <div ref={carouselRef} className={cx('trendsSlider__carousel')}>
          {trends.map((trend, index) => (
            <div
              key={index}
              ref={el => {
                cardRefs.current[index] = el;
              }}
              className={cx('trendsSlider__cardWrapper')}
            >
              <button
                type="button"
                className={cx('trendsSlider__card', {
                  trendsSlider__cardSelected: index === selectedIndex
                })}
                onClick={() => handleCardClick(index)}
                aria-pressed={index === selectedIndex}
                aria-label={`Trend ${trend.displayName}`}
              >
                <Image
                  src={trend.image}
                  alt={trend.displayName}
                  className={cx('trendsSlider__cardImage')}
                />
                <div className={cx('trendsSlider__cardOverlay')}>{trend.displayName}</div>
              </button>
            </div>
          ))}
        </div>
        {selectedTrend && (
          <div className={cx('trendsSlider__content')}>
            <h2 className={cx('trendsSlider__hashtag')}>{selectedTrend.displayName}</h2>
            <p className={cx('trendsSlider__description')}>{selectedTrend.description}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendsPLPSlider;
